import { hashWords, toUnitFloat } from "daedalus-core";
import { durationMs } from "odyssey-shared";
import { type Crackle, type VfxDefinition, type VfxLayer } from "../definition.ts";

/**
 * The `xpBarSurge` vfx (P2.4-T53, ADR-134): the HUD's XP bar at a level-up, an explosion in time with the `levelUp`
 * burst. The bar is drawn full while the level-up toast names the level, so the surge lasts exactly that long: the toast's
 * entry, its hold and its blink, read from the motion table. At 0 ms the whole bar flashes gold-white once, a glow spills
 * past its edges and 32 sparks and embers (3 to 4 pixels) erupt up and out of it in arcs 80 to 120 pixels high; the flash
 * and the glow step down to a steady wash within 650 ms and do not return. A wide band runs along the bar four times from
 * 200 ms. Through the hold the bar stays steadily lit, and 16 crackles and three small bursts of 5 sparks carry the
 * motion; the wash ends as the bar drops back.
 * It is for the page's own bar only: no one else sees another player's bar. Under reduced motion it is one still pulse
 * held for the same time. The crackles' places come from the shared seeded hash.
 */

const SEED = 0x78706172;
const LIFE_MS = durationMs("toast-in") + durationMs("toast-hold") + durationMs("blink");
const WAVE_FROM_MS = 200;
const WAVE_RUN_MS = 900;
const WAVE_RUNS = 4;
const WAVE_WIDTH_PX = 16;
const BLAST_CRACKLES = 32;
const BLAST_SPREAD_MS = 220;
const AMBIENT_CRACKLES = 16;
const BURST_AT_MS = [2550, 3450, 3700] as const;
const BURST_CRACKLES = 5;
const words = new Uint32Array(2);

/**
 * A number from 0 up to 1 for one number of a crackle, from the shared hash.
 *
 * @param index The crackle.
 * @param which Which number of it.
 * @returns The unit float.
 */
function unit(index: number, which: number): number {
  words[0] = index;
  words[1] = which;
  return toUnitFloat(hashWords(SEED, words, 2));
}

/**
 * The pulse's keys, in steps: one flash to gold-white at the blast that steps down through the glow rings to a steady
 * wash within 650 ms, held unchanged through the hold, then nothing at the end of the life.
 *
 * @returns The `[ms, strength]` keys, in time order.
 */
function pulseKeys(): [number, number][] {
  return [
    [0, 0],
    [60, 4],
    [200, 4],
    [400, 3],
    [550, 2],
    [650, 1],
    [LIFE_MS - 150, 1],
    [LIFE_MS, 0],
  ];
}

/**
 * The blast's crackles: sparks and embers that leave the whole bar together, flying up and out in arcs 80 to 120 pixels
 * high and up to about 110 across, away from the bar's middle, 3 to 4 pixels square, then falling back.
 *
 * @returns The crackles.
 */
function blast(): Crackle[] {
  const list: Crackle[] = [];
  for (let index = 0; index < BLAST_CRACKLES; index += 1) {
    const at = unit(index, 1);
    const outward = at < 0.5 ? -1 : 1;
    list.push({
      delayMs: Math.floor(unit(index, 0) * BLAST_SPREAD_MS),
      lifeMs: 900 + Math.floor(unit(index, 4) * 700),
      at,
      rise: -(80 + Math.floor(unit(index, 2) * 41)),
      drift: Math.round(outward * (20 + unit(index, 3) * 90) * (0.4 + Math.abs(at - 0.5))),
      fall: 20 + Math.floor(unit(index, 5) * 25),
      size: index % 3 === 0 ? 4 : 3,
      light: index % 2 === 0 ? 1 : 0,
    });
  }
  return list;
}

/**
 * The ambient crackles of the hold: one every few hundred milliseconds along the bar, rising 20 to 50 pixels.
 *
 * @returns The crackles.
 */
function ambient(): Crackle[] {
  const list: Crackle[] = [];
  const from = 1200;
  const room = LIFE_MS - from - 800;
  for (let index = 0; index < AMBIENT_CRACKLES; index += 1) {
    const key = BLAST_CRACKLES + index;
    list.push({
      delayMs: from + Math.floor(((index + unit(key, 0)) / AMBIENT_CRACKLES) * room),
      lifeMs: 800,
      at: unit(key, 1),
      rise: -(20 + Math.floor(unit(key, 2) * 31)),
      drift: Math.round((unit(key, 3) - 0.5) * 40),
      fall: 8 + Math.floor(unit(key, 5) * 14),
      size: index % 4 === 0 ? 3 : 2,
      light: index % 2 === 0 ? 1 : 0,
    });
  }
  return list;
}

/**
 * The small bursts of the hold: a few sparks leaving one place on the bar together, rising 40 to 70 pixels.
 *
 * @returns The crackles.
 */
function bursts(): Crackle[] {
  const list: Crackle[] = [];
  BURST_AT_MS.forEach((time, burst) => {
    const centre = 0.15 + unit(900 + burst, 1) * 0.7;
    for (let spark = 0; spark < BURST_CRACKLES; spark += 1) {
      const key = BLAST_CRACKLES + AMBIENT_CRACKLES + burst * BURST_CRACKLES + spark;
      list.push({
        delayMs: time - 100 + Math.floor(unit(key, 0) * 120),
        lifeMs: 800,
        at: Math.min(1, Math.max(0, centre + (unit(key, 1) - 0.5) * 0.12)),
        rise: -(40 + Math.floor(unit(key, 2) * 31)),
        drift: Math.round((unit(key, 3) - 0.5) * 60),
        fall: 12 + Math.floor(unit(key, 5) * 18),
        size: spark % 2 === 0 ? 3 : 2,
        light: spark % 2 === 0 ? 1 : 0,
      });
    }
  });
  return list;
}

/**
 * The `xpBarSurge` definition.
 */
export const XP_BAR_SURGE: VfxDefinition = Object.freeze({
  id: "xpBarSurge",
  lifeMs: LIFE_MS,
  target: "xpBar",
  others: false,
  sound: Object.freeze({ own: null, others: null }),
  layers: Object.freeze<VfxLayer[]>([
    { kind: "wave", fromMs: WAVE_FROM_MS, toMs: WAVE_FROM_MS + WAVE_RUNS * WAVE_RUN_MS, widthPx: WAVE_WIDTH_PX, runs: WAVE_RUNS },
    { kind: "pulse", keys: pulseKeys(), peak: 4 },
    { kind: "crackle", fromMs: 0, spanMs: LIFE_MS, crackles: [...blast(), ...ambient(), ...bursts()] },
  ]),
  reduced: Object.freeze({ lifeMs: LIFE_MS, flashMs: LIFE_MS, pillarHalf: 0, auraStep: 0, edgeStep: 0, pulseStep: 1, textStill: false }),
});
